import React, { createContext, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { Button, Space, Spin, Typography, Result, ConfigProvider, theme as antdTheme } from 'antd';
import { loginRequest } from './authConfig';
import { syncMyAvatar } from './utils/avatarSync';
import { loadTheme, saveTheme, THEME_LOADED } from './utils/themeSync';
import './LoginSplash.css';
import api, { setGoogleCredential, clearGoogleCredential, getGoogleCredential, getGoogleCredentialExp } from './api';
import { setAppTimezone } from './utils/time';
import Layout from './components/Layout';
import SearchPage from './pages/SearchPage';
import CallDetailPage from './pages/CallDetailPage';
import AdminPage from './pages/AdminPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CasesPage from './pages/CasesPage';
import CasePage from './pages/CasePage';
import EvalFormsPage from './pages/EvalFormsPage';
import EvalFormEditorPage from './pages/EvalFormEditorPage';
import EvalAgentStatsPage from './pages/EvalAgentStatsPage';
import EvalInsightsPage from './pages/EvalInsightsPage';
import ErrorInsightsPage from './pages/ErrorInsightsPage';
import ScheduledStaffingPage from './pages/ScheduledStaffingPage';
import HeadcountPage from './pages/HeadcountPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectPage from './pages/ProjectPage';
import ProjectTasksPage from './pages/ProjectTasksPage';
import CampaignUpdatesPage from './pages/CampaignUpdatesPage';
import EvalPrintAllPage from './pages/EvalPrintAllPage';
import EvalAIScoreAuditPage from './pages/EvalAIScoreAuditPage';
import PageErrorBoundary from './components/PageErrorBoundary';
import EvalEvaluatorStatsPage from './pages/EvalEvaluatorStatsPage';
import CalibrationPage from './pages/CalibrationPage';
import CalibrationSessionPage from './pages/CalibrationSessionPage';
import EvalAnalyticsPage from './pages/EvalAnalyticsPage';
import CXOneScorecardsPage from './pages/CXOneScorecardsPage';
import AgentsPageNew from './pages/AgentsPageNew';
import AgentSummaryPageNew from './pages/AgentSummaryPageNew';
import AlertConfigPage from './pages/AlertConfigPage';
import AlertsPage from './pages/AlertsPage';
import RawDataPage from './pages/RawDataPage';
import CoachingSessionsPage   from './pages/CoachingSessionsPage';
import CoachingAgentStatsPage from './pages/CoachingAgentStatsPage';
import CoachingCoachStatsPage from './pages/CoachingCoachStatsPage';
import CoachingSessionPage    from './pages/CoachingSessionPage';
import CoachingConfigPage     from './pages/CoachingConfigPage';
import MyCoachingSessionsPage  from './pages/MyCoachingSessionsPage';
import EvalQAComparisonPage   from './pages/EvalQAComparisonPage';
import FeedbackOnlyPage       from './pages/FeedbackOnlyPage';
import MyEvalsPage            from './pages/MyEvalsPage';
import MaintenancePage        from './pages/MaintenancePage';
import AccountLockedPage      from './pages/AccountLockedPage';
import LeaderboardPage        from './pages/LeaderboardPage';
import SidekickPage           from './pages/SidekickPage';
import SidekickConfigPage     from './pages/SidekickConfigPage';
import NotificationsPage      from './pages/NotificationsPage';
import ErrorsPage             from './pages/ErrorsPage';
import ErrorSubmissionPage    from './pages/ErrorSubmissionPage';
import MyErrorsPage           from './pages/MyErrorsPage';
import ErrorAnalyticsPage     from './pages/ErrorAnalyticsPage';
import ErrorAgentStatsPage        from './pages/ErrorAgentStatsPage';
import ErrorEvaluatorStatsPage    from './pages/ErrorEvaluatorStatsPage';
import ErrorRawDataPage       from './pages/ErrorRawDataPage';
import CampaignsAdminPage              from './pages/CampaignsAdminPage';
import TeamsAdminPage                  from './pages/TeamsAdminPage';
import SkillExclusionsAdminPage        from './pages/SkillExclusionsAdminPage';
import SkillGroupsAdminPage            from './pages/SkillGroupsAdminPage';
import WFMSkillsConfigPage             from './pages/WFMSkillsConfigPage';
import WFMSkillsAssignPage             from './pages/WFMSkillsAssignPage';
import AvailabilityPage                from './pages/AvailabilityPage';
import ShiftBoardPage                  from './pages/ShiftBoardPage';
import ShiftSwapForumPage              from './pages/ShiftSwapForumPage';
import ShiftSwapExplorePage            from './pages/ShiftSwapExplorePage';
import ShiftDepartmentEligibilityPage  from './pages/ShiftDepartmentEligibilityPage';
import AbsenceNotificationsPage        from './pages/AbsenceNotificationsPage';
import MyAbsenceNotificationsPage      from './pages/MyAbsenceNotificationsPage';
import MyCampaignUpdatesPage           from './pages/MyCampaignUpdatesPage';
import MyHelpdeskPage                  from './pages/MyHelpdeskPage';
import MyHelpdeskClientsPage           from './pages/MyHelpdeskClientsPage';
import AbsenceNotificationPage         from './pages/AbsenceNotificationPage';
import ScheduleVariancesPage           from './pages/ScheduleVariancesPage';
import ScheduleVariancePage            from './pages/ScheduleVariancePage';
import ComplianceListPage              from './pages/ComplianceListPage';
import IncidentStatsPage               from './pages/IncidentStatsPage';
import ComplianceDetailPage            from './pages/ComplianceDetailPage';
import MyComplianceIssuesPage          from './pages/MyComplianceIssuesPage';
import ComplianceConfigPage            from './pages/ComplianceConfigPage';
import GradeWeightsAdminPage          from './pages/GradeWeightsAdminPage';
import CCaaSStatusesAdminPage         from './pages/CCaaSStatusesAdminPage';
import BadgesAdminPage                from './pages/BadgesAdminPage';
import RecapTemplatesPage             from './pages/RecapTemplatesPage';
import HelpDeskQueuesPage             from './pages/HelpDeskQueuesPage';
import HelpDeskAutomationsPage        from './pages/HelpDeskAutomationsPage';
// Loaded on its own: its diagram library is only needed there.
const HelpDeskWorkflowsPage = lazy(() => import('./pages/HelpDeskWorkflowsPage'));
const LabelMakerPage = lazy(() => import('./pages/LabelMakerPage'));
import ClientsPage                    from './pages/ClientsPage';
import ClientDetailPage               from './pages/ClientDetailPage';
import ClientContactPage              from './pages/ClientContactPage';
import GenerateRecapPage              from './pages/GenerateRecapPage';
import CNEMonthlyStatsUploadPage      from './pages/CNEMonthlyStatsUploadPage';
import CNEDailyStatsUploadPage        from './pages/CNEDailyStatsUploadPage';
import CNELoginUploadPage             from './pages/CNELoginUploadPage';
import CNEMonthlyQAStatsUploadPage    from './pages/CNEMonthlyQAStatsUploadPage';
import CNECSATUploadPage             from './pages/CNECSATUploadPage';
import PrincipalBillUploadPage        from './pages/PrincipalBillUploadPage';
import BCBSBillUploadPage             from './pages/BCBSBillUploadPage';
import PayrollGLUploadPage            from './pages/PayrollGLUploadPage';
import CostRevenuePage                from './pages/CostRevenuePage';
import DBALThresholdsAdminPage        from './pages/DBALThresholdsAdminPage';
import LockDatesAdminPage             from './pages/LockDatesAdminPage';
import EmailConfigPage                from './pages/EmailConfigPage';
import DBALPage                        from './pages/DBALPage';
import CampaignStatsPage               from './pages/CampaignStatsPage';
import CampaignWeeklyScorecardPage     from './pages/CampaignWeeklyScorecardPage';
import CampaignSummaryPage            from './pages/CampaignSummaryPage';
import CampaignDispoStatsPage          from './pages/CampaignDispoStatsPage';
import CampaignAgentStatsPage          from './pages/CampaignAgentStatsPage';
import CampaignStatsTodayPage          from './pages/CampaignStatsTodayPage';
import ErlangStatsPage                 from './pages/ErlangStatsPage';
import EmployeeCensusPage              from './pages/EmployeeCensusPage';
import SchedulePage                    from './pages/SchedulePage';
import AdherencePage                   from './pages/AdherencePage';
import FTEForecastPage                 from './pages/FTEForecastPage';
import EmployeeJobsAdminPage           from './pages/EmployeeJobsAdminPage';
import ProjectTypesAdminPage           from './pages/ProjectTypesAdminPage';
import CampaignUpdatesConfigPage       from './pages/CampaignUpdatesConfigPage';
import CampaignUpdatesUploadPage       from './pages/CampaignUpdatesUploadPage';
import StarsAdherenceUploadPage        from './pages/StarsAdherenceUploadPage';
import EmployeeHistoryPage             from './pages/EmployeeHistoryPage';
import EngagementsPage                from './pages/EngagementsPage';
import EngagementLeaderboardPage      from './pages/EngagementLeaderboardPage';
import MyEngagementsPage              from './pages/MyEngagementsPage';
import CampaignGoalsAdminPage          from './pages/CampaignGoalsAdminPage';
import TimecardSummaryPage             from './pages/TimecardSummaryPage';
import WallOfFamePage                  from './pages/WallOfFamePage';
import CheersPage                      from './pages/CheersPage';
import CheersConfigPage                from './pages/CheersConfigPage';
import EvalProgressPage                from './pages/EvalProgressPage';
import EvalCriteriaPage               from './pages/EvalCriteriaPage';
import TeamTimelinePage               from './pages/TeamTimelinePage';
import TeamCCaaSStatsPage            from './pages/TeamCCaaSStatsPage';
import HelpdeskPage                   from './pages/HelpdeskPage';
import HelpdeskTicketPage             from './pages/HelpdeskTicketPage';
import RecruitmentStatsPage          from './pages/RecruitmentStatsPage';
import AttritionPage                 from './pages/AttritionPage';
import CNEMonthlyStatsPage           from './pages/CNEMonthlyStatsPage';
import CNEDailyStatsPage             from './pages/CNEDailyStatsPage';
import IdeasPage                     from './pages/IdeasPage';
import IdeaDetailPage                from './pages/IdeaDetailPage';
import ConHomeUpsellImportPage       from './pages/ConHomeUpsellImportPage';
import ConHomeUpsellStatsPage        from './pages/ConHomeUpsellStatsPage';

// Shared context so any component can read the current user's role and name
export const UserContext = createContext(null);

// Google only accepts sign-ins from the origins registered on the OAuth
// client, and that is harbor.superiorcontact.net. The app also answers on its
// azurewebsites.net address, where the button would render and then fail when
// clicked -- so it is offered only where it can work. Microsoft login is
// unaffected and works on both.
//
// localhost is here for development against a client that lists it.
const GOOGLE_SIGNIN_HOSTS = ['harbor.superiorcontact.net', 'localhost'];

function googleSignInClientId() {
  const id = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return id && GOOGLE_SIGNIN_HOSTS.includes(window.location.hostname) ? id : null;
}

function LoginPage({ onGoogleSuccess }) {
  const { instance } = useMsal();
  const googleBtnRef = useRef(null);

  useEffect(() => {
    const googleClientId = googleSignInClientId();
    if (!googleClientId) return;

    const init = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          setGoogleCredential(response.credential);
          onGoogleSuccess(response.credential);
        },
        auto_select: false,
      });
      if (googleBtnRef.current) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          shape: 'pill',
          size: 'large',
          width: 240,
          text: 'signin_with',
        });
      }
    };

    if (window.google?.accounts?.id) {
      init();
    } else {
      // GSI script not yet loaded — attach to its load callback
      const prev = window.onGoogleLibraryLoad;
      window.onGoogleLibraryLoad = () => { if (prev) prev(); init(); };
    }
  }, [onGoogleSuccess]);

  const googleClientId = googleSignInClientId();

  // Sign-in buttons lean toward the pointer and spring back when it leaves.
  const rootRef = useRef(null);
  useEffect(() => {
    const reduce = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cleanups = [];
    rootRef.current?.querySelectorAll('.lp-wobble-hit').forEach((hit) => {
      const el = hit.firstElementChild;
      const p = { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, raf: 0 };
      const step = () => {
        p.vx = (p.vx + (p.tx - p.x) * 0.12) * 0.82;
        p.vy = (p.vy + (p.ty - p.y) * 0.12) * 0.82;
        p.x += p.vx;
        p.y += p.vy;
        const sp = Math.hypot(p.vx, p.vy);
        const on = p.tx || p.ty ? 0.04 : 0;
        const sx = 1 + Math.min(sp * 0.02, 0.18) + on;
        const sy = 1 - Math.min(sp * 0.015, 0.12) + on;
        el.style.transform = `translate(${p.x}px,${p.y}px) rotate(${p.vx * 0.6}deg) skewX(${-p.vx * 0.8}deg) scale(${sx},${sy})`;
        const atRest = sp < 0.02 && Math.abs(p.tx - p.x) < 0.05 && Math.abs(p.ty - p.y) < 0.05;
        if (atRest && !p.tx && !p.ty) { p.raf = 0; el.style.transform = ''; } else p.raf = requestAnimationFrame(step);
      };
      const kick = () => { if (!p.raf) p.raf = requestAnimationFrame(step); };
      const move = (e) => {
        if (reduce()) return;
        const r = hit.getBoundingClientRect();
        p.tx = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width / 2))) * 14;
        p.ty = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height / 2))) * 10;
        kick();
      };
      const leave = () => {
        p.vx += (Math.random() - 0.5) * 10;
        p.vy += (Math.random() - 0.5) * 6;
        p.tx = 0;
        p.ty = 0;
        kick();
      };
      hit.addEventListener('pointermove', move);
      hit.addEventListener('pointerenter', move);
      hit.addEventListener('pointerleave', leave);
      cleanups.push(() => {
        cancelAnimationFrame(p.raf);
        hit.removeEventListener('pointermove', move);
        hit.removeEventListener('pointerenter', move);
        hit.removeEventListener('pointerleave', leave);
      });
    });
    return () => cleanups.forEach((fn) => fn());
  }, [googleClientId]);

  const wavePath = 'M0,60 C150,110 350,10 600,60 C850,110 1050,10 1200,60 C1350,110 1550,10 1800,60 C2050,110 2250,10 2400,60 L2400,200 L0,200 Z';

  return (
    <div className="lp-root" ref={rootRef}>
      <div className="lp-light lp-light-a" />
      <div className="lp-light lp-light-b" />
      <div className="lp-waves" aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <svg key={n} className={`lp-wave lp-wave-${n}`} viewBox="0 0 2400 200" preserveAspectRatio="none">
            <path d={wavePath} />
          </svg>
        ))}
      </div>
      <main className="lp-stage">
        <div className="lp-logo-wrap">
          <span className="lp-ring lp-ring-1" />
          <span className="lp-ring lp-ring-2" />
          <span className="lp-ring lp-ring-3" />
          <div className="lp-logo"><img src="/sharborLogo.png" alt="Harbor" /></div>
        </div>
        <div className="lp-actions">
          {googleClientId && (
            <div className="lp-group lp-group-1">
              <div className="lp-label">
                Non-Admins such as Agents, Support Agents, QA &mdash; log in with your Google Account
              </div>
              <div className="lp-wobble-hit">
                <div className="lp-wobble"><div className="lp-google" ref={googleBtnRef} /></div>
              </div>
            </div>
          )}
          <div className={`lp-group ${googleClientId ? 'lp-group-2' : 'lp-group-1'}`}>
            <div className="lp-label">
              {googleClientId
                ? 'All other users login with your SuperiorContact.Net Microsoft 365 login'
                : 'Please login with your SuperiorContact.Net Microsoft 365 Account'}
            </div>
            <div className="lp-wobble-hit">
              <div className="lp-wobble">
                <button className="lp-ms" type="button" onClick={() => instance.loginRedirect(loginRequest)}>
                  Sign in with Microsoft
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// A Google sign-in token lasts an hour and nothing renewed it, so Google users
// were sent back to the login page hourly. Shortly before it expires -- and
// whenever the tab comes back into view near expiry -- ask Google to re-issue
// it silently (automatic sign-in for a returning user). If Google cannot, for
// instance the user has signed out of Google, nothing changes: the token runs
// out and they sign in again as before.
const GOOGLE_RENEW_BEFORE_MS = 5 * 60 * 1000;

function useGoogleTokenRenewal(googleToken, onNewToken) {
  useEffect(() => {
    const clientId = googleSignInClientId();
    if (!googleToken || !clientId) return undefined;

    let timer = null;
    const renew = () => {
      const g = window.google?.accounts?.id;
      if (!g) return;
      g.initialize({
        client_id: clientId,
        auto_select: true,
        use_fedcm_for_prompt: true,
        callback: (response) => {
          setGoogleCredential(response.credential);
          onNewToken(response.credential);
        },
      });
      g.prompt();
    };
    const msLeft = () => {
      const exp = getGoogleCredentialExp();
      return exp ? exp * 1000 - Date.now() : 0;
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(renew, Math.max(0, msLeft() - GOOGLE_RENEW_BEFORE_MS));
    };
    // Timers stall while a laptop sleeps or a tab is in the background, so
    // check again whenever the tab is shown.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (msLeft() < GOOGLE_RENEW_BEFORE_MS) renew(); else schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [googleToken, onNewToken]);
}

function AuthRouter() {
  const { accounts } = useMsal();
  const [googleToken, setGoogleToken] = useState(() => getGoogleCredential());

  const handleGoogleSuccess = useCallback((token) => setGoogleToken(token), []);
  // Only for a Google session: a Microsoft account renews through MSAL.
  useGoogleTokenRenewal(accounts.length === 0 ? googleToken : null, handleGoogleSuccess);

  const isAuth = accounts.length > 0 || !!googleToken;
  if (!isAuth) return <LoginPage onGoogleSuccess={handleGoogleSuccess} />;
  return <AppContent googleToken={googleToken} />;
}

// Per-page grants from UserPageGrants. Additive only: every call site reads
// `existing rule || hasGrant(...)`, so a grant can widen access, never narrow it.
export const hasGrant = (u, key) => !!u?.PageGrants?.includes(key);

// Projects Access (CanEditProjects): 1 View uses Projects, 2 Edit also creates
// Folders; admins always; agents never.
export const canUseProjects = (r, u) => r !== 'agent' && (r === 'admin' || (u?.CanEditProjects ?? 0) >= 1);

// Campaign Updates Access (CanEditCampaignUpdates): 1 View, 2 Edit, 3 Edit
// Manager; admins always. Acknowledging your own needs none of it.
export const canUseCampaignUpdates = (r, u) => r === 'admin' || (u?.CanEditCampaignUpdates ?? 0) >= 1;

// /projects/:id?folder=N (before Folder and Project swapped names on screen)
// -> /folders/:id?project=N.
function OldFolderLink() {
  const { id } = useParams();
  const project = new URLSearchParams(useLocation().search).get('folder');
  return <Navigate to={`/folders/${id}${project ? `?project=${project}` : ''}`} replace />;
}

function Guard({ children, allow, fallback }) {
  const currentUser = React.useContext(UserContext);
  if (!currentUser) return <Spin />;
  // The page they land on says why they are there (Layout reads the state).
  if (!allow(currentUser.Role, currentUser)) return <Navigate to={fallback ?? '/agent-summary'} replace state={{ notice: 'denied' }} />;
  return children;
}

const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

function AppContent({ googleToken }) {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const { accounts, instance } = useMsal();
  // Whether there is a Google sign-in, not the token itself: a renewed token
  // must not reload the user and everything that hangs off it.
  const hasGoogle = !!googleToken;

  useEffect(() => {
    // In mock mode there is no MSAL login, but the backend returns a mock user
    if (accounts.length === 0 && !hasGoogle && !MOCK_MODE) { setUserLoading(false); return; }
    api.get('/api/me')
      .then((res) => { setCurrentUser(res.data); setAppTimezone(res.data.AppTimezone); syncMyAvatar(); loadTheme(res.data.ID); })
      .catch((err) => {
        // auth.py sends exactly this for IsLocked. A locked user has no
        // currentUser, so the /account-locked route inside Layout never
        // renders; the page has to be shown from here.
        if (err.response?.status === 403 && err.response?.data?.error === 'Your account is inactive.') setAccountLocked(true);
        // A non-agent who signed in with Google: tell them which login to use.
        else if (err.response?.status === 403 && err.response?.data?.code === 'google_role_denied') setAccessDenied('google_role');
        else if (err.response?.status === 403) setAccessDenied(true);
        setCurrentUser(null);
      })
      .finally(() => setUserLoading(false));
  }, [accounts, hasGoogle]);

  if (userLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 120 }}>
        <Spin size="large" tip="Loading…" />
      </div>
    );
  }

  if (accountLocked) return <AccountLockedPage />;

  if (accessDenied) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle={accessDenied === 'google_role'
          ? 'Your role requires you to log in with your SuperiorContact.Net Microsoft 365 account instead of Google.'
          : 'Your account is not authorized to access this application. Contact your administrator.'}
        extra={
          <Button onClick={() => { clearGoogleCredential(); instance.clearCache(); window.location.replace(window.location.origin); }}>
            Sign out
          </Button>
        }
        style={{ marginTop: 80 }}
      />
    );
  }

  return (
    <UserContext.Provider value={currentUser}>
      <Layout>
        {/* Inside the Layout on purpose. A render error here takes the page
            and leaves the menu, so whoever hits it can navigate away instead
            of facing a white screen. Keyed on the path so moving to another
            page clears it -- a boundary that has caught stays caught until
            its key changes. */}
        <PageErrorBoundary key={location.pathname}>
        {/* A client's own login (Role 'client'): Campaign Summary, and with
            their permissions Search, Campaign Stats, Agent Summary, WFM Skills
            Assign and Campaign Updates, and My HelpDesk --
            every other address lands on Campaign Summary.
            The server allows them only what these pages call (auth.py
            _client_refusal). */}
        {currentUser?.Role === 'client' ? (
        <Routes>
          <Route path="/campaign-summary/:campaignId?" element={<CampaignSummaryPage />} />
          <Route path="/search"           element={<Guard allow={(r, u) => (u?.CanSearchCalls ?? 0) >= 1} fallback="/campaign-summary"><SearchPage /></Guard>} />
          <Route path="/campaign-stats"   element={<Guard allow={(r, u) => (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/campaign-summary"><CampaignStatsPage /></Guard>} />
          <Route path="/campaign-stats-today" element={<Guard allow={(r, u) => (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/campaign-summary"><CampaignStatsTodayPage /></Guard>} />
          <Route path="/my-helpdesk"      element={<MyHelpdeskClientsPage />} />
          {/* Detail pages, read-only, each with its area's View. */}
          <Route path="/cases/:caseId"    element={<Guard allow={(r, u) => (u?.CanEditEvals ?? 0) >= 1} fallback="/campaign-summary"><CasePage /></Guard>} />
          <Route path="/errors/:id"       element={<Guard allow={(r, u) => (u?.CanEditErrors ?? 0) >= 1} fallback="/campaign-summary"><ErrorSubmissionPage /></Guard>} />
          <Route path="/coaching/sessions/:sessionId" element={<Guard allow={(r, u) => (u?.CanEditCoaching ?? 0) >= 1} fallback="/campaign-summary"><CoachingSessionPage /></Guard>} />
          <Route path="/compliance/incidents/:id" element={<Guard allow={(r, u) => (u?.CanEditIncidents ?? 0) >= 1} fallback="/campaign-summary"><ComplianceDetailPage type="incident" /></Guard>} />
          <Route path="/helpdesk/:id"     element={<HelpdeskTicketPage />} />
          <Route path="/calls/:contactId" element={<Guard allow={(r, u) => (u?.CanSearchCalls ?? 0) >= 1} fallback="/campaign-summary"><CallDetailPage /></Guard>} />
          <Route path="/agents"           element={<Guard allow={(r, u) => (u?.CanViewAgentSummary ?? 0) >= 1} fallback="/campaign-summary"><AgentsPageNew /></Guard>} />
          <Route path="/wfm-skills"       element={<Guard allow={(r, u) => (u?.CanEditWFMSkills ?? 0) >= 1} fallback="/campaign-summary"><WFMSkillsAssignPage /></Guard>} />
          <Route path="/campaign-updates" element={<Guard allow={(r, u) => canUseCampaignUpdates(r, u)} fallback="/campaign-summary"><CampaignUpdatesPage /></Guard>} />
          <Route path="*" element={<Navigate to="/campaign-summary" replace />} />
        </Routes>
        ) : (
        <Routes>
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/account-locked" element={<AccountLockedPage />} />
          <Route path="/" element={<Navigate to="/agent-summary" replace />} />
          <Route path="/search"           element={<Guard allow={(r, u) => r !== 'agent' && ((u?.CanEditEvals ?? 0) >= 1 || (u?.CanSearchCalls ?? 0) >= 1)} fallback="/agent-summary"><SearchPage /></Guard>} />
          <Route path="/calls/:contactId" element={<CallDetailPage />} />
          <Route path="/analytics"        element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewAIAnalytics ?? 0) >= 1} fallback="/agent-summary"><AnalyticsPage /></Guard>} />
          <Route path="/campaign-stats"          element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignStatsPage /></Guard>} />
          <Route path="/campaign-weekly-scorecard" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignWeeklyScorecardPage /></Guard>} />
          {/* Same flag as the campaign pages it gathers. Each tab mirrors its
              own page's guard on top of this, so a user sees only the tabs
              they could have opened directly. The campaign is in the path so
              a tab can be linked to; without one the page asks. */}
          {/* One route with the campaign optional: two routes made picking the
              first campaign remount the page and drop the team picked. */}
          <Route path="/campaign-summary/:campaignId?" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignSummaryPage /></Guard>} />
          <Route path="/campaign-dispo-stats"  element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignDispoStatsPage /></Guard>} />
          <Route path="/campaign-agent-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignAgentStatsPage /></Guard>} />
          <Route path="/campaign-stats-today" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><CampaignStatsTodayPage /></Guard>} />
          <Route path="/erlang-stats"    element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1} fallback="/agent-summary"><ErlangStatsPage /></Guard>} />
          <Route path="/cne-monthly-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1 && (!(u?.VisibleTeams?.length) || u.VisibleTeams.some(t => t.ID === 3))} fallback="/agent-summary"><CNEMonthlyStatsPage /></Guard>} />
          <Route path="/cne-daily-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewCampaignAnalytics ?? 0) >= 1 && (!(u?.VisibleTeams?.length) || u.VisibleTeams.some(t => t.ID === 3))} fallback="/agent-summary"><CNEDailyStatsPage /></Guard>} />
          <Route path="/cases"            element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/my-evals"><CasesPage /></Guard>} />
          <Route path="/cases/:caseId"    element={<CasePage />} />
          <Route path="/eval-agent-stats"     element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><EvalAgentStatsPage /></Guard>} />
          <Route path="/eval-insights"        element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><EvalInsightsPage /></Guard>} />
          <Route path="/eval-ai-audit"        element={<Guard allow={(r) => ['admin', 'qa_admin'].includes(r)} fallback="/agent-summary"><EvalAIScoreAuditPage /></Guard>} />
          <Route path="/eval-progress"        element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><EvalProgressPage /></Guard>} />
          <Route path="/eval-print"           element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><EvalPrintAllPage /></Guard>} />
          <Route path="/eval-evaluator-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 2} fallback="/agent-summary"><EvalEvaluatorStatsPage /></Guard>} />
          <Route path="/calibration"          element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 2} fallback="/agent-summary"><CalibrationPage /></Guard>} />
          <Route path="/calibration/:sessionId" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 2} fallback="/agent-summary"><CalibrationSessionPage /></Guard>} />
          <Route path="/eval-analytics"       element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><EvalAnalyticsPage /></Guard>} />
          <Route path="/cxone-scorecard-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><CXOneScorecardsPage /></Guard>} />
          <Route path="/eval-forms"       element={<Guard allow={(r, u) => (u?.CanConfigEvals ?? 0) >= 1}><EvalFormsPage /></Guard>} />
          <Route path="/eval-forms/:formId/edit" element={<Guard allow={(r, u) => (u?.CanConfigEvals ?? 0) >= 1}><EvalFormEditorPage /></Guard>} />
          <Route path="/agent-summary"    element={<AgentSummaryPageNew />} />
          <Route path="/agents"           element={<Guard allow={(r, u) => (u?.CanViewAgentSummary ?? 0) >= 1} fallback="/agent-summary"><AgentsPageNew /></Guard>} />
          <Route path="/admin"                  element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditUsers ?? 0) >= 1}><AdminPage /></Guard>} />
          <Route path="/admin/alert-config"    element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><AlertConfigPage /></Guard>} />
          <Route path="/admin/coaching-config" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><CoachingConfigPage /></Guard>} />
          <Route path="/alerts"               element={<AlertsPage />} />
          <Route path="/eval-raw-data"        element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><RawDataPage /></Guard>} />
          <Route path="/coaching/sessions"            element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditCoaching ?? 0) >= 1} fallback="/coaching/my-sessions"><CoachingSessionsPage /></Guard>} />
          <Route path="/coaching/agent-stats"         element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditCoaching ?? 0) >= 1} fallback="/coaching/my-sessions"><CoachingAgentStatsPage /></Guard>} />
          <Route path="/coaching/coach-stats"         element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditCoaching ?? 0) >= 1} fallback="/coaching/my-sessions"><CoachingCoachStatsPage /></Guard>} />
          <Route path="/coaching/sessions/:sessionId" element={<CoachingSessionPage />} />
          <Route path="/coaching/my-sessions"         element={<MyCoachingSessionsPage />} />
          <Route path="/eval-qa-comparison"           element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 2} fallback="/agent-summary"><EvalQAComparisonPage /></Guard>} />
          <Route path="/feedback-only"               element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditEvals ?? 0) >= 1} fallback="/agent-summary"><FeedbackOnlyPage /></Guard>} />
          <Route path="/my-evals"                    element={<MyEvalsPage />} />
          <Route path="/leaderboard"               element={<Guard allow={(r, u) => r !== 'agent' || !!u?.LeaderboardAgentsEnabled} fallback="/agent-summary"><LeaderboardPage /></Guard>} />
          <Route path="/wall-of-fame"              element={<WallOfFamePage />} />
          <Route path="/cheers"                    element={<CheersPage />} />
          <Route path="/admin/cheers-config"       element={<Guard allow={(r) => ['admin','qa_admin'].includes(r)} fallback="/agent-summary"><CheersConfigPage /></Guard>} />
          <Route path="/sidekick"                  element={<Guard allow={(r) => r === 'admin'}><SidekickPage /></Guard>} />
          <Route path="/admin/sidekick-config"     element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><SidekickConfigPage /></Guard>} />
          <Route path="/admin/campaigns"           element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><CampaignsAdminPage /></Guard>} />
          <Route path="/admin/teams"               element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><TeamsAdminPage /></Guard>} />
          <Route path="/admin/skill-exclusions"    element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><SkillExclusionsAdminPage /></Guard>} />
          <Route path="/admin/skill-groups"        element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><SkillGroupsAdminPage /></Guard>} />
          <Route path="/admin/wfm-skills"          element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditWFMSkills ?? 0) >= 3}><WFMSkillsConfigPage /></Guard>} />
          <Route path="/notifications"             element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditIncidents ?? 0) >= 1} fallback="/agent-summary"><NotificationsPage /></Guard>} />
          <Route path="/notifications/:id"         element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditIncidents ?? 0) >= 1} fallback="/agent-summary"><NotificationsPage /></Guard>} />
          <Route path="/errors"                   element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditErrors ?? 0) >= 1} fallback="/my-errors"><ErrorsPage /></Guard>} />
          <Route path="/errors/:id"               element={<ErrorSubmissionPage />} />
          <Route path="/error-agent-stats"         element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditErrors ?? 0) >= 1} fallback="/my-errors"><ErrorAgentStatsPage /></Guard>} />
          <Route path="/error-evaluator-stats"     element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditErrors ?? 0) >= 2} fallback="/my-errors"><ErrorEvaluatorStatsPage /></Guard>} />
          <Route path="/error-insights"          element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditErrors ?? 0) >= 1} fallback="/my-errors"><ErrorInsightsPage /></Guard>} />
          <Route path="/error-analytics"          element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditErrors ?? 0) >= 1} fallback="/my-errors"><ErrorAnalyticsPage /></Guard>} />
          <Route path="/error-raw-data"           element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><ErrorRawDataPage /></Guard>} />
          <Route path="/my-errors"                element={<MyErrorsPage />} />
          <Route path="/absences"              element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditAbsences ?? 0) >= 1} fallback="/my-absences"><AbsenceNotificationsPage /></Guard>} />
          <Route path="/my-absences"           element={<MyAbsenceNotificationsPage />} />
          <Route path="/my-campaign-updates"   element={<MyCampaignUpdatesPage />} />
          <Route path="/my-helpdesk"           element={<MyHelpdeskPage />} />
          <Route path="/absences/:id"          element={<AbsenceNotificationPage />} />
          {/* WFM Absence Swap List: admins and Absences access 3 and up. */}
          <Route path="/shift-board"            element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditAbsences ?? 0) >= 3}><ShiftBoardPage /></Guard>} />
          {/* Shift Swap Forum: open by URL; the menu entry shows for WFM Skills 3 only, for now. */}
          <Route path="/shift-swap-forum"         element={<ShiftSwapForumPage />} />
          <Route path="/shift-swap-forum/explore" element={<ShiftSwapExplorePage />} />
          <Route path="/admin/shift-department-eligibility" element={<Guard allow={(r) => r === 'admin'}><ShiftDepartmentEligibilityPage /></Guard>} />
          <Route path="/wfm-skills"             element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditWFMSkills ?? 0) >= 1}><WFMSkillsAssignPage /></Guard>} />
          {/* Employee Availability: WFM Skills access. */}
          <Route path="/availability"           element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditWFMSkills ?? 0) >= 1}><AvailabilityPage /></Guard>} />
          <Route path="/schedule-variances"     element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditScheduleVariance ?? 0) >= 1}><ScheduleVariancesPage /></Guard>} />
          <Route path="/schedule-variances/:id" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditScheduleVariance ?? 0) >= 1}><ScheduleVariancePage /></Guard>} />
          <Route path="/compliance/incidents"       element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditIncidents ?? 0) >= 1} fallback="/agent-summary"><ComplianceListPage type="incident" /></Guard>} />
          <Route path="/incident-stats"             element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditIncidents ?? 0) >= 1} fallback="/agent-summary"><IncidentStatsPage /></Guard>} />
          <Route path="/compliance/incidents/:id"   element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditIncidents ?? 0) >= 1} fallback="/agent-summary"><ComplianceDetailPage type="incident" /></Guard>} />
          <Route path="/compliance/my-issues"       element={<MyComplianceIssuesPage />} />
          <Route path="/compliance/my-issues/:id"   element={<ComplianceDetailPage type="incident" />} />
          <Route path="/compliance/my-writeups/:id" element={<ComplianceDetailPage type="writeup" />} />
          <Route path="/compliance/writeups"        element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditFormalWriteups ?? 0) >= 1} fallback="/agent-summary"><ComplianceListPage type="writeup" /></Guard>} />
          <Route path="/compliance/writeups/:id"    element={<Guard allow={(r, u) => r === 'admin' || (u?.CanEditFormalWriteups ?? 0) >= 1} fallback="/agent-summary"><ComplianceDetailPage type="writeup" /></Guard>} />
          <Route path="/admin/compliance-config"    element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><ComplianceConfigPage /></Guard>} />
          <Route path="/admin/grade-weights"        element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><GradeWeightsAdminPage /></Guard>} />
          <Route path="/admin/ccaas-statuses"      element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><CCaaSStatusesAdminPage /></Guard>} />
          <Route path="/admin/badges"              element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><BadgesAdminPage /></Guard>} />
          <Route path="/admin/recap-templates"     element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><RecapTemplatesPage /></Guard>} />
          <Route path="/coaching/generate-recap"    element={<Guard allow={(r, u) => (u?.CanEditEvals ?? 0) >= 3 && (u?.CanEditCoaching ?? 0) >= 2} fallback="/coaching/my-sessions"><GenerateRecapPage /></Guard>} />
          <Route path="/admin/cne-upload"           element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 3) || hasGrant(u, 'cne-upload')}><CNEMonthlyStatsUploadPage /></Guard>} />
          <Route path="/admin/cne-daily-upload"     element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 3) || hasGrant(u, 'cne-daily-upload')}><CNEDailyStatsUploadPage /></Guard>} />
          <Route path="/admin/cne-login-upload"     element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 3) || hasGrant(u, 'cne-login-upload')}><CNELoginUploadPage /></Guard>} />
          <Route path="/admin/cne-qa-upload"        element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 3) || hasGrant(u, 'cne-qa-upload')}><CNEMonthlyQAStatsUploadPage /></Guard>} />
          <Route path="/admin/cne-csat-upload"      element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 3) || hasGrant(u, 'cne-csat-upload')}><CNECSATUploadPage /></Guard>} />
          <Route path="/admin/principal-bill"      element={<Guard allow={(r, u) => r === 'admin' || hasGrant(u, 'principal-bill')}><PrincipalBillUploadPage /></Guard>} />
          <Route path="/admin/bcbs-bill"           element={<Guard allow={(r, u) => r === 'admin' || hasGrant(u, 'bcbs-bill')}><BCBSBillUploadPage /></Guard>} />
          <Route path="/admin/payroll-gl"          element={<Guard allow={(r, u) => !!u?.CanViewPayrollFinancials}><PayrollGLUploadPage /></Guard>} />
          <Route path="/cost-revenue"              element={<Guard allow={(r, u) => !!u?.CanViewPayrollFinancials}><CostRevenuePage /></Guard>} />
          <Route path="/admin/dbal-thresholds"     element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><DBALThresholdsAdminPage /></Guard>} />
          <Route path="/admin/lock-dates"          element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><LockDatesAdminPage /></Guard>} />
          <Route path="/admin/email-config"        element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><EmailConfigPage /></Guard>} />
          <Route path="/admin/engagements"                     element={<Guard allow={(r, u) => (u?.CanEditEngagements ?? 0) >= 1}><EngagementsPage /></Guard>} />
          <Route path="/admin/engagements/:id/leaderboard" element={<Guard allow={(r, u) => (u?.CanEditEngagements ?? 0) >= 1}><EngagementLeaderboardPage /></Guard>} />
          <Route path="/my-engagements"                      element={<MyEngagementsPage />} />
          <Route path="/my-engagements/:id/leaderboard"     element={<EngagementLeaderboardPage />} />
          <Route path="/dbal"                      element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewOversightStats ?? 0) >= 1} fallback="/agent-summary"><DBALPage /></Guard>} />
          <Route path="/census"                    element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewRecruitmentStats ?? 0) >= 1} fallback="/agent-summary"><EmployeeCensusPage /></Guard>} />
          <Route path="/recruitment-stats"        element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewRecruitmentStats ?? 0) >= 1} fallback="/agent-summary"><RecruitmentStatsPage /></Guard>} />
          {/* Projects: Projects Access View or Edit (or admin); never agents. */}
          <Route path="/projects"               element={<Guard allow={(r, u) => canUseProjects(r, u)} fallback="/agent-summary"><ProjectsPage /></Guard>} />
          {/* A Folder's page (on screen the top level is a Folder). /projects/:id
              was its address before the rename; old links redirect. */}
          <Route path="/folders/:id"            element={<Guard allow={(r, u) => canUseProjects(r, u)} fallback="/agent-summary"><ProjectDetailPage /></Guard>} />
          <Route path="/folders/:id/projects/:projectId" element={<Guard allow={(r, u) => canUseProjects(r, u)} fallback="/agent-summary"><ProjectPage /></Guard>} />
          <Route path="/projects/:id"           element={<OldFolderLink />} />
          <Route path="/campaign-updates"       element={<Guard allow={(r, u) => canUseCampaignUpdates(r, u)} fallback="/agent-summary"><CampaignUpdatesPage /></Guard>} />
          <Route path="/project-tasks"          element={<Guard allow={(r, u) => canUseProjects(r, u)} fallback="/agent-summary"><ProjectTasksPage /></Guard>} />
          <Route path="/headcount"               element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewRecruitmentStats ?? 0) >= 1} fallback="/agent-summary"><HeadcountPage /></Guard>} />
          <Route path="/attrition"               element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewRecruitmentStats ?? 0) >= 1} fallback="/agent-summary"><AttritionPage /></Guard>} />
          <Route path="/break-lunch-schedule"       element={<SchedulePage />} />
          <Route path="/compliance/adherence"      element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewOversightStats ?? 0) >= 1} fallback="/agent-summary"><AdherencePage /></Guard>} />
          <Route path="/compliance/timecard-summary" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditForecasting ?? 0) >= 1} fallback="/agent-summary"><TimecardSummaryPage /></Guard>} />
          <Route path="/scheduled-staffing"       element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewOversightStats ?? 0) >= 1} fallback="/agent-summary"><ScheduledStaffingPage /></Guard>} />
          <Route path="/compliance/team-timeline"    element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewOversightStats ?? 0) >= 1} fallback="/agent-summary"><TeamTimelinePage /></Guard>} />
          <Route path="/compliance/team-ccaas-stats" element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanViewOversightStats ?? 0) >= 1} fallback="/agent-summary"><TeamCCaaSStatsPage /></Guard>} />
          <Route path="/fte-forecast"              element={<Guard allow={(r, u) => r !== 'agent' && (u?.CanEditForecasting ?? 0) >= 1} fallback="/agent-summary"><FTEForecastPage /></Guard>} />
          <Route path="/admin/campaign-updates-upload" element={<Guard allow={(r) => r === 'admin'}><CampaignUpdatesUploadPage /></Guard>} />
          <Route path="/admin/stars-adherence-upload" element={<Guard allow={(r, u) => r === 'admin' || hasGrant(u, 'stars-adherence-upload')}><StarsAdherenceUploadPage /></Guard>} />
          <Route path="/admin/campaign-updates-config" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><CampaignUpdatesConfigPage /></Guard>} />
          <Route path="/admin/project-types"       element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><ProjectTypesAdminPage /></Guard>} />
          <Route path="/admin/employee-jobs"       element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><EmployeeJobsAdminPage /></Guard>} />
          <Route path="/admin/employee-history"   element={<Guard allow={(r, u) => (u?.CanEditStaffHistory ?? 0) >= 1}><EmployeeHistoryPage /></Guard>} />
          <Route path="/admin/goals"             element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><CampaignGoalsAdminPage /></Guard>} />
          <Route path="/admin/eval-criteria"    element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><EvalCriteriaPage /></Guard>} />
          <Route path="/helpdesk"     element={<Guard allow={(r, u) => !!u?.HelpDeskSearch}><HelpdeskPage /></Guard>} />
          <Route path="/admin/helpdesk-queues" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><HelpDeskQueuesPage /></Guard>} />
          <Route path="/admin/helpdesk-automations" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><HelpDeskAutomationsPage /></Guard>} />
          <Route path="/admin/helpdesk-automations/:id" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><HelpDeskAutomationsPage /></Guard>} />
          <Route path="/admin/helpdesk-workflows" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><Suspense fallback={<Spin />}><HelpDeskWorkflowsPage /></Suspense></Guard>} />
          <Route path="/admin/helpdesk-workflows/:id" element={<Guard allow={(r) => r === 'admin' || r === 'qa_admin'}><Suspense fallback={<Spin />}><HelpDeskWorkflowsPage /></Suspense></Guard>} />
          <Route path="/admin/clients" element={<Guard allow={(r, u) => (u?.CanEditClients ?? 0) >= 1}><ClientsPage /></Guard>} />
          <Route path="/admin/clients/:id" element={<Guard allow={(r, u) => (u?.CanEditClients ?? 0) >= 1}><ClientDetailPage /></Guard>} />
          <Route path="/admin/client-contacts/:id" element={<Guard allow={(r, u) => (u?.CanEditClients ?? 0) >= 1}><ClientContactPage /></Guard>} />
          <Route path="/helpdesk/:id" element={<HelpdeskTicketPage />} />
          <Route path="/ideas"     element={<IdeasPage />} />
          <Route path="/ideas/:id" element={<IdeaDetailPage />} />
          <Route path="/admin/conhome-upsell-import" element={<Guard allow={(r, u) => r === 'admin' || r === 'qa_admin' || (u?.JobID === 6 && u?.TeamID === 1) || hasGrant(u, 'conhome-upsell-import')}><ConHomeUpsellImportPage /></Guard>} />
          <Route path="/apps/label-maker" element={<Suspense fallback={<Spin />}><LabelMakerPage /></Suspense>} />
          <Route path="/upsell-stats" element={<Guard allow={(r, u) => (r === 'agent' && u?.TeamID === 1) || r !== 'agent'} fallback="/agent-summary"><ConHomeUpsellStatsPage /></Guard>} />
          <Route path="*" element={<Result status="404" title="Page Not Found" subTitle="The page you're looking for doesn't exist." extra={<Button onClick={() => window.history.back()}>Go Back</Button>} />} />
        </Routes>
        )}
        </PageErrorBoundary>
      </Layout>
    </UserContext.Provider>
  );
}

const SEGMENTED_THEME = {
  token: {
    colorPrimary: '#e8682a',
    borderRadius:  4,
  },
  components: {
    // The selected option is the solid orange one; the rest sit quietly on a
    // light grey track, so which is chosen reads at a glance.
    Segmented: {
      trackBg:        '#ececec',
      itemSelectedBg: '#e8682a',
      itemHoverBg:    '#dcdcdc',
      itemSelectedColor: '#ffffff',
      itemColor:      '#595959',
    },
  },
};

// Dark mode: the user menu's toggle, remembered in this browser. Ant Design's
// dark algorithm does the components; index.css (html[data-theme="dark"])
// does what is styled by hand -- the light backgrounds, dark text and light
// borders written inline across the pages.
export const ThemeModeContext = createContext({ dark: false, setDark: () => {} });
const THEME_KEY = 'harbor-theme';

function readDark() {
  try { return localStorage.getItem(THEME_KEY) === 'dark'; } catch { return false; }
}

function buildTheme(dark) {
  if (!dark) return SEGMENTED_THEME;
  return {
    ...SEGMENTED_THEME,
    algorithm: antdTheme.darkAlgorithm,
    components: {
      ...SEGMENTED_THEME.components,
      Segmented: {
        ...SEGMENTED_THEME.components.Segmented,
        trackBg:     '#2a2a2a',
        itemHoverBg: '#3a3a3a',
        itemColor:   'rgba(255,255,255,0.65)',
      },
    },
  };
}

export default function App() {
  const [darkPref, setDarkState] = useState(readDark);
  // Printing always uses the light look: dark panels on white paper, with
  // white labels lost against it, printed badly. The page switches to light
  // just before the print preview is drawn (flushSync, so it is ready in
  // time) and back afterwards; the saved choice is not touched.
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const before = () => {
      document.documentElement.dataset.theme = 'light';
      flushSync(() => setPrinting(true));
    };
    const after = () => setPrinting(false);
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => { window.removeEventListener('beforeprint', before); window.removeEventListener('afterprint', after); };
  }, []);
  const dark = darkPref && !printing;
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  }, [dark]);
  const mode = useMemo(() => ({
    dark,
    setDark: (v) => {
      setDarkState(v);
      try { localStorage.setItem(THEME_KEY, v ? 'dark' : 'light'); } catch { /* private window */ }
      saveTheme();
    },
  }), [dark]);
  // The user's saved theme, once they are signed in (utils/themeSync).
  useEffect(() => {
    const reload = () => setDarkState(readDark());
    window.addEventListener(THEME_LOADED, reload);
    return () => window.removeEventListener(THEME_LOADED, reload);
  }, []);
  const themeConfig = useMemo(() => buildTheme(dark), [dark]);

  return (
    <ThemeModeContext.Provider value={mode}>
      <ConfigProvider theme={themeConfig}>
        <BrowserRouter>
          {/* Mock mode: skip Azure AD entirely and go straight to the app */}
          {MOCK_MODE ? <AppContent /> : <AuthRouter />}
        </BrowserRouter>
      </ConfigProvider>
    </ThemeModeContext.Provider>
  );
}
